import React, { useRef, useState, useContext, useCallback, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LinePlaneIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import { sum } from 'lib/array/arrayUtils'
import Modal from 'LuxKit/Modal/Modal'
import { FlightsClassTypes } from 'constants/flight'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import OfferAirportOptionsGroupedList from 'components/OfferPage/Filters/Airport/OfferAirportOptionsGroupedList'
import { charterFareCabinLabelMap, CHARTER_BUNDLE_DURATION_OPTIONS, MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES } from 'constants/charterFlight'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import config from 'constants/config'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import MaldivesOccupancySelect from './MaldivesOccupancySelect'
import Backdrop from 'components/utils/Backdrop'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import FloatingTooltip from 'LuxKit/FloatingTooltip'
import { useAppSelector } from 'hooks/reduxHooks'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { smartScrollIntoViewIfNeeded } from 'lib/window/scrollUtils'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import NoAirportsOptionListItem from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOptionListItem'
import CharterDurationToggleControl from 'components/OfferPage/Filters/CharterFlight/CharterDurationToggleControl'
import { ALL_CHARTER_FARE_CLASSES, getDefaultFareClassFromAllowed } from 'lib/charterFlights/charterOfferFareClassRestrictions'

const BundleSearchSection = styled.section`
  padding-top: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rem(12)};
  
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(12)};
  }
  
  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(12)};
  }
`

const FilterListItem = styled(ClickableListItem)<{ $mobileFullWidth?: boolean }>`
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  
  /* Increase specificity to override default padding */
  && {
    padding-block: ${rem(10)};
    padding-inline: ${rem(16)};
  }
  
  /* Override the uppercase transformation from the overline */
  & * {
    text-transform: none !important;
  }
  
  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.five};
  }

  ${props => props.$mobileFullWidth && css`
    grid-column: span 2;
  `}

  ${mediaQueryUp.tablet} {
    grid-column: auto;
  }
`

// Props interface
interface MaldivesSearchState {
  rooms: Array<App.Occupants>
  selectedBundle: string
  departureAirport: App.Airport
  flightClass?: FlightsClassTypes
  luxPlusEnabled: boolean
  checkInDate?: string
  checkOutDate?: string
  departDate?: string
  returnDate?: string
}

interface Props {
  searchState: MaldivesSearchState
  onRoomsChange: (rooms: Array<App.Occupants>) => void
  onRoomsApply: (rooms: Array<App.Occupants>) => void
  onBundleChange: (bundle: string) => void
  onAirportChange: (airport: App.Airport) => void
  onFlightClassChange: (flightClass: FlightsClassTypes) => void
  onDateSelect: (departDate: string, returnDate: string) => void
  onOpenFlightCalendar: () => void
  availableFareClasses: Array<FlightsClassTypes>
}

type OpenFilter = 'travellers' | 'airport' | undefined

const hiddenMelbourneAirportCodes = new Set<string>(MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES) // Charter departs from MEL only

function MaldivesDirectSearchFilters(props: Props) {
  const {
    searchState,
    onRoomsChange,
    onRoomsApply,
    onBundleChange,
    onAirportChange,
    onFlightClassChange,
    onOpenFlightCalendar,
    availableFareClasses,
  } = props
  const [openFilter, setOpenFilter] = useState<OpenFilter>(undefined)
  const intlDateFormatter = useIntlDateFormatter()

  const filtersGridRef = useRef<HTMLDivElement>(null)
  const filtersBlockRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLButtonElement>(null)
  const airportButtonRef = useRef<HTMLButtonElement>(null)
  const datesButtonRef = useRef<HTMLButtonElement>(null)
  const travellersDropdownRef = useRef<HTMLDivElement>(null)
  const airportDropdownRef = useRef<HTMLDivElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')
  const [airportSearchPhrase, setAirportSearchPhrase] = useState('')

  const openModal = useContext(ModalContext)

  const australianAirports = useAppSelector(state => state.geo.originAirports)
  const popularDepartureAirports = usePopularAirports('departure')
  const airportRanking = useMemo(() => {
    const ranking = new Map<string, number>()
    popularDepartureAirports.forEach((airport, index) => {
      ranking.set(airport.airportCode, index)
    })
    return ranking
  }, [popularDepartureAirports])
  const durationToggleOptions = useMemo(() => CHARTER_BUNDLE_DURATION_OPTIONS.map(option => ({
    label: option.label,
    value: option.value,
  })), [])
  const airports = useMemo(() => {
    return australianAirports
      .filter(airport => airport.countryCode === 'AU' && !hiddenMelbourneAirportCodes.has(airport.code))
      .slice()
      .sort((a, b) => {
        const rankA = airportRanking.get(a.code)
        const rankB = airportRanking.get(b.code)

        if (rankA !== undefined && rankB !== undefined) {
          return rankA - rankB
        }

        if (rankA !== undefined) {
          return -1
        }

        if (rankB !== undefined) {
          return 1
        }

        return a.name.localeCompare(b.name)
      })
  }, [australianAirports, airportRanking])
  const filteredAirports = useMemo(() => {
    const normalizedValue = airportSearchPhrase.trim().toLowerCase()
    if (!normalizedValue) {
      return airports
    }
    return airports.filter(airport => `${airport.name} ${airport.code}`.toLowerCase().includes(normalizedValue))
  }, [airports, airportSearchPhrase])
  const showNoAirportResults = !!airportSearchPhrase && filteredAirports.length === 0

  const handleDurationToggleChange = useCallback((selectedNights: number) => {
    if (selectedNights === 12) {
      onBundleChange('12-nights')
      return
    }
    onBundleChange('5-nights')
  }, [onBundleChange])

  // Scroll dropdown into view when it opens
  useEffect(() => {
    if (!openFilter || !isTabletScreenSizeAndUp) {
      return
    }

    // Wait for Floating UI to position the dropdown
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const elementToScroll =
          openFilter === 'travellers' ? travellersDropdownRef.current :
            openFilter === 'airport' ? airportDropdownRef.current :
              null

        if (elementToScroll) {
          smartScrollIntoViewIfNeeded(elementToScroll)
        }
      })
    })
  }, [openFilter, isTabletScreenSizeAndUp])

  function handleTravellersToggle() {
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    setOpenFilter(prev => prev === 'travellers' ? undefined : 'travellers')
  }

  function closeTravellersMenu() {
    setOpenFilter(undefined)
  }

  function handleRoomChange(newRooms: Array<App.Occupants>) {
    onRoomsChange(newRooms)
  }

  function handleRoomsApply() {
    onRoomsApply(searchState.rooms)
    closeTravellersMenu()
  }

  const handleAirportSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAirportSearchPhrase(event.target.value)
  }, [])

  function handleAirportToggle() {
    if (isAgentHubEnabled) {
      return
    }
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    setOpenFilter(prev => prev === 'airport' ? undefined : 'airport')
  }

  function closeAirportDropdown() {
    setOpenFilter(undefined)
    setAirportSearchPhrase('')
  }

  const handleAirportSelect = useCallback((airport: App.AirportLocation) => {
    onAirportChange({
      code: airport.airportCode,
      name: airport.airportName,
      latitude: airport.lt!,
      longitude: airport.lg!,
      countryCode: airport.country,
    })
    closeAirportDropdown()
  }, [onAirportChange])

  const getAirportOptionsList = useCallback((withRadioButtons?: boolean) => {
    if (showNoAirportResults) {
      return <NoAirportsOptionListItem inputValue={airportSearchPhrase} />
    }
    return (
      <OfferAirportOptionsGroupedList
        airports={filteredAirports}
        handleItemClick={handleAirportSelect}
        selectedAirport={searchState.departureAirport}
        withRadioButtons={withRadioButtons}
      />
    )
  }, [airportSearchPhrase, filteredAirports, handleAirportSelect, searchState.departureAirport, showNoAirportResults])

  const handleClassSelect = useCallback((fareClass: FlightsClassTypes) => {
    onFlightClassChange(fareClass)
  }, [onFlightClassChange])

  const effectiveAvailableFareClasses = useMemo(() => {
    if (availableFareClasses.length > 0) {
      return availableFareClasses
    }
    return ALL_CHARTER_FARE_CLASSES
  }, [availableFareClasses])
  const canOpenFlightClassModal = useMemo(
    () => effectiveAvailableFareClasses.length > 1,
    [effectiveAvailableFareClasses.length],
  )
  const classEndIcon = useMemo(
    () => (canOpenFlightClassModal ? <LineAngleDownIcon /> : undefined),
    [canOpenFlightClassModal],
  )
  const modalSelectedClass = useMemo<FlightsClassTypes>(() => {
    return searchState.flightClass ??
      getDefaultFareClassFromAllowed(effectiveAvailableFareClasses) ??
      effectiveAvailableFareClasses[0] ??
      FlightsClassTypes.ECONOMY
  }, [effectiveAvailableFareClasses, searchState.flightClass])

  const openFlightClassModal = async() => {
    if (!canOpenFlightClassModal) {
      return
    }
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    const selectedClass = await openModal<FlightsClassTypes>(<MaldivesCharterFlightFareClassModal
      selectedClassId={modalSelectedClass}
      checkInDate={searchState.checkInDate}
      checkOutDate={searchState.checkOutDate}
      occupancies={searchState.rooms}
      allowedFareClasses={effectiveAvailableFareClasses}
    />)

    if (selectedClass) {
      handleClassSelect(selectedClass)
    }
  }

  // Open flight calendar modal (now handled at page level)
  function handleDatesToggle() {
    // Scroll filter button into view when clicked
    if (isTabletScreenSizeAndUp && filtersBlockRef.current) {
      smartScrollIntoViewIfNeeded(filtersBlockRef.current)
    }
    onOpenFlightCalendar()
  }

  const containsChildren = searchState.rooms.some(room => (room.children ?? 0) > 0)
  const containsInfants = searchState.rooms.some(room => (room.infants ?? 0) > 0)
  const guestCount = sum(searchState.rooms, room => room.adults + (room.children ?? 0) + (room.infants ?? 0))

  const travellersDisplayText = searchState.rooms.length ?
      pluralizeToString((containsChildren || containsInfants) ? 'guest' : 'adult', guestCount) :
    'Select guests'

  const selectedClassLabel = searchState.flightClass
    ? charterFareCabinLabelMap[searchState.flightClass]
    : 'Select cabin'

  // Calculate nights based on selected bundle
  const nights = searchState.selectedBundle === '5-nights' ? 5 : 12

  // Format selected dates for display
  // For charter bundle, display flight dates (departDate/returnDate) instead of hotel dates (checkIn/checkOut)
  // Match the style from CharterFlightCalendarSelect: use intlDateFormatter, bold the date text, and style nights as thin grey
  const datesDisplayText = useMemo(() => {
    // Use flight dates if available (charter bundle mode), otherwise fall back to hotel dates
    if (searchState.departDate && searchState.returnDate) {
      const dateText = `${intlDateFormatter(searchState.departDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(searchState.returnDate, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> <BodyText variant="medium" weight="normal" colour="neutral-two" as="span">{nightsText}</BodyText></>
    }
    if (searchState.checkInDate && searchState.checkOutDate) {
      const dateText = `${intlDateFormatter(searchState.checkInDate, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(searchState.checkOutDate, INTL_DAY_MONTH_NAME_SHORT)}`
      const nightsText = `(${pluralizeToString('night', nights)})`
      return <><b>{dateText}</b> <BodyText variant="medium" weight="normal" colour="neutral-two" as="span">{nightsText}</BodyText></>
    }
    return 'Select dates'
  }, [searchState.checkInDate, searchState.checkOutDate, searchState.departDate, searchState.returnDate, nights, intlDateFormatter])

  const selectedDepartureAirport = useMemo(() => {
    return airports.find(airport => airport.code === searchState.departureAirport.code)
  }, [airports, searchState.departureAirport.code])

  return (
    <BundleSearchSection>
      <LayoutContainer ref={filtersGridRef}>
        <Group direction="vertical" gap={20}>
          <Heading id="maldives-direct-search-filters-heading" variant="heading2" as="h2" align="center">
            Exclusive hotel + flight bundles
          </Heading>

          <Group direction="vertical" gap={12}>
            <Group direction="horizontal" horizontalAlign="center">
              <CharterDurationToggleControl
                options={durationToggleOptions}
                selectedValue={nights}
                onChange={handleDurationToggleChange}
              />
            </Group>

            <ScrollAnchor ref={filtersBlockRef} vOffset={rem(12)} />
            <FiltersGrid>
              {/* Guests Filter */}
              <FilterListItem
                size="large"
                ref={inputRef}
                onClick={handleTravellersToggle}
                startIcon={<LineUserIcon />}
                endIcon={<LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  Guests
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {travellersDisplayText}
                  </BodyText>
                }
              />

              {/* Class Filter */}
              <FilterListItem
                size="large"
                onClick={openFlightClassModal}
                startIcon={<LineSeatIcon />}
                endIcon={classEndIcon}
                overline={<BodyText variant="small" weight="normal" as="span">
                  Class
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {selectedClassLabel}
                  </BodyText>
                }
              />

              {/* Departure Airport Filter */}
              <FilterListItem
                $mobileFullWidth
                size="large"
                ref={airportButtonRef}
                onClick={handleAirportToggle}
                startIcon={<LinePlaneIcon />}
                endIcon={isAgentHubEnabled ? undefined : <LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  Departure Airport
                </BodyText>}
                title={
                  <>
                    <BodyText variant="medium" weight="bold" as="span">
                      {searchState.departureAirport.name}
                    </BodyText>
                    <BodyText variant="medium" weight="normal" as="span">
                      {` (${searchState.departureAirport.code})`}
                    </BodyText>
                  </>
                }
                style={isAgentHubEnabled ? { cursor: 'not-allowed' } : undefined}
              />

              {/* When (Dates) Filter - Opens Modal at page level */}
              <FilterListItem
                $mobileFullWidth
                size="large"
                ref={datesButtonRef}
                onClick={handleDatesToggle}
                startIcon={<LineCalendarIcon />}
                endIcon={<LineAngleDownIcon />}
                overline={<BodyText variant="small" weight="normal" as="span">
                  When
                </BodyText>}
                title={
                  <BodyText variant="medium" weight="bold" as="span">
                    {datesDisplayText}
                  </BodyText>
                }
              />
            </FiltersGrid>
          </Group>
          {isAgentHubEnabled && (
            <FloatingTooltip
              triggerRef={airportButtonRef}
              description={
                <Group direction="vertical" gap={8}>
                  <BodyText variant="small" weight="normal" as="span">This flight only departs from {selectedDepartureAirport?.name} {selectedDepartureAirport?.code} - any flights to or from {selectedDepartureAirport?.code} must be purchased separately.</BodyText>
                  <BodyText variant="small" weight="normal" as="span">Please ensure you leave enough time for transfers - you are responsible for arriving for this flight on time. Missed flights will not be refunded.</BodyText>
                </Group>
              }
              placement="bottom"
              portaled
            />
          )}
        </Group>
      </LayoutContainer>

      {/* Desktop Occupancy Select */}
      <MaldivesOccupancySelect
        rooms={searchState.rooms}
        open={openFilter === 'travellers' && !isMobile}
        onRoomChange={handleRoomChange}
        onApply={handleRoomsApply}
        onClose={closeTravellersMenu}
        triggerRef={inputRef}
        boundaryRef={filtersGridRef as React.RefObject<HTMLElement>}
        dropdownRef={travellersDropdownRef}
      />
      {/* Mobile Occupancy Modal */}
      <Modal
        mode="drawer"
        title="Guests"
        isOpen={openFilter === 'travellers' && isMobile}
        onClose={closeTravellersMenu}
        onBack={closeTravellersMenu}
        primaryActionText="Apply"
        onPrimaryActionClick={handleRoomsApply}
        headerExtension={
          <Group direction="horizontal" horizontalAlign="end">
            <BodyText variant="large">
              <FormattedOccupancy
                occupancies={searchState.rooms}
                saleUnit="room"
              />
            </BodyText>
          </Group>
        }
      >
        <OccupancyPicker
          saleUnit="room"
          occupancies={searchState.rooms}
          onChange={handleRoomChange}
          enableMultiRoom={config.accommodation.allowsMultiRoom}
          childVariant="child-age"
        />
      </Modal>

      {/* Desktop Airport Selection Dropdown */}
      <DropdownList
        ref={airportDropdownRef}
        size="M"
        open={openFilter === 'airport' && !isMobile}
        anchorRef={airportButtonRef}
        triggerRef={airportButtonRef}
        title="Where will you depart from?"
        subtitle="Select your preferred airport"
        onClose={closeAirportDropdown}
        primaryActionProps={{
          children: 'Apply',
          onClick: closeAirportDropdown,
        }}
        headerExtension={<TextInputOnly
          placeholder="Search by city or airport"
          noValidationMessage
          noValidationSpacing
          value={airportSearchPhrase}
          onChange={handleAirportSearchChange}
          endIcon={<LineSearchIcon />}
        />}
      >
        {getAirportOptionsList()}
      </DropdownList>
      <Backdrop visible={!!(openFilter === 'airport' && !isMobile)} onClick={closeAirportDropdown} data-testid="airport-select-backdrop" />

      {/* Mobile Airport Selection Modal */}
      <Modal
        mode="drawer"
        title="Where will you depart from?"
        isOpen={openFilter === 'airport' && isMobile}
        onClose={closeAirportDropdown}
        onBack={closeAirportDropdown}
        flushListItems
      >
        <VerticalSpacer gap={12}>
          <TextInputOnly
            placeholder="Search by city or airport"
            noValidationMessage
            noValidationSpacing
            value={airportSearchPhrase}
            onChange={handleAirportSearchChange}
            endIcon={<LineSearchIcon />}
          />
          {getAirportOptionsList()}
        </VerticalSpacer>
      </Modal>
    </BundleSearchSection>
  )
}

export default MaldivesDirectSearchFilters
